/**
 * ModuleErrorBoundary — Phase 75
 *
 * React Error Boundary that wraps module layout shells (Cash Flow, Inventory,
 * HR) to catch unexpected render-time and child-component errors and present
 * a graceful, branded fallback INSIDE the app shell rather than letting the
 * exception crash the React tree all the way up to the router catch-all (`*`)
 * which currently redirects to the public landing page (`/`).
 *
 * ─── Why a class component ─────────────────────────────────────────────────
 * React 18/19 still requires class components for `componentDidCatch` and
 * `getDerivedStateFromError`.  There is no hooks-based equivalent.
 *
 * ─── What it catches ───────────────────────────────────────────────────────
 *   • Unhandled exceptions thrown during render of child components.
 *   • Errors thrown from lifecycle methods of class descendants.
 *   • Errors thrown from constructors of descendants.
 *
 * What it does NOT catch (by React's own contract):
 *   • Event handler errors  — those are surfaced via the standard window
 *     'error' event and should be handled at the call site.
 *   • Asynchronous code (setTimeout, fetch.then errors, etc.) — those must
 *     be caught by the calling effect / try-catch.
 *   • Server-side rendering errors (n/a — we're a SPA).
 *   • Errors thrown by the boundary itself.
 *
 * ─── Recovery affordances ─────────────────────────────────────────────────
 *   1. "נסה שוב" — bumps an internal key to force-remount the children.
 *   2. "חזרה למודל פעיל" — uses the moduleHomePath prop (e.g. /app/cashflow)
 *      to navigate the user to a known-good landing within the module.
 *   3. "חזרה לדף הבית" — last-resort link back to /app.
 *
 * ─── Privacy / observability ──────────────────────────────────────────────
 * The full error message + stack are written to console.error so they show
 * up in the browser devtools and in Sentry-style frontend observability
 * pipelines.  We deliberately surface the message text to the user as well
 * (collapsible) because it dramatically speeds up Eli-style production
 * debugging — but it is hidden behind a `<details>` summary so a casual user
 * is not greeted by a stack trace.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, ArrowRight } from 'lucide-react';

interface ModuleErrorBoundaryProps {
  /**
   * Hebrew label of the wrapped module, e.g. "ניהול מלאי".  Shown in the
   * banner so the user knows which module crashed.
   */
  moduleLabel: string;

  /**
   * Path to the module's "safe" landing page (typically the module index).
   * Clicked when the user hits "חזרה למודל פעיל".  Using a normal anchor +
   * full-page reload (window.location.href) is intentional — it discards
   * any corrupted in-memory React state from the crashed subtree, which is
   * the most reliable recovery for hard render errors.
   */
  moduleHomePath: string;

  children: ReactNode;
}

interface ModuleErrorBoundaryState {
  /** Increments on every "try again" press to force-remount the subtree. */
  remountKey: number;
  /** The captured error.  null when no error is active. */
  error: Error | null;
  /** React's component stack (whose render threw, with parent chain). */
  componentStack: string | null;
}

export class ModuleErrorBoundary extends Component<
  ModuleErrorBoundaryProps,
  ModuleErrorBoundaryState
> {
  state: ModuleErrorBoundaryState = {
    remountKey:     0,
    error:          null,
    componentStack: null,
  };

  // React lifecycle: derive state from an error so the next render shows
  // the fallback UI.  Pure function — no side-effects allowed here.
  static getDerivedStateFromError(error: Error): Partial<ModuleErrorBoundaryState> {
    return { error };
  }

  // React lifecycle: side-effects (logging) belong here.
  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(
      '[Phase 75 ModuleErrorBoundary] module="%s" caught an error:\n%s\n\nComponent stack:%s',
      this.props.moduleLabel,
      error?.stack ?? String(error),
      info.componentStack ?? '(no component stack)',
    );
    this.setState({ componentStack: info.componentStack ?? null });
  }

  private handleRetry = () => {
    // Clear the error AND bump remountKey so the children remount fresh.
    this.setState(prev => ({
      remountKey:     prev.remountKey + 1,
      error:          null,
      componentStack: null,
    }));
  };

  private handleGoModuleHome = () => {
    // Full-page navigation is intentional — wipes any corrupted React state.
    window.location.href = this.props.moduleHomePath;
  };

  private handleGoAppHome = () => {
    window.location.href = '/app';
  };

  render(): ReactNode {
    const { error, componentStack, remountKey } = this.state;
    const { moduleLabel, moduleHomePath, children } = this.props;

    if (!error) {
      // Wrap children in a fragment keyed by remountKey so "try again" forces
      // a full remount — children's useState / useEffect are reset.
      return <div key={remountKey}>{children}</div>;
    }

    // ── Graceful fallback view ────────────────────────────────────────
    return (
      <div dir="rtl" className="flex items-start justify-center px-4 py-10 sm:py-16">
        <div className="w-full max-w-2xl bg-white border border-gray-200 rounded-3xl shadow-sm overflow-hidden">

          {/* Banner */}
          <div className="flex items-center gap-4 px-6 sm:px-8 py-5 border-b border-gray-100 bg-gradient-to-l from-red-50 to-white">
            <div className="shrink-0 w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-base sm:text-lg font-bold text-gray-900">
                אירעה שגיאה בלתי צפויה במודל
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5 truncate">
                {moduleLabel}
              </p>
            </div>
          </div>

          {/* Message */}
          <div className="px-6 sm:px-8 py-6 space-y-3">
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
              לא הצלחנו להציג את העמוד עקב שגיאה פנימית. הנתונים שלך לא נפגעו —
              ניתן לנסות שוב, לחזור למודל פעיל, או לדווח על הבעיה לתמיכה.
            </p>

            {/* Collapsible diagnostic — hidden by default */}
            <details className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 group">
              <summary className="cursor-pointer text-xs font-semibold text-gray-600 hover:text-gray-800 select-none">
                פרטים טכניים (לתמיכה)
              </summary>
              <div className="mt-3 space-y-2">
                <p className="text-[11px] font-mono break-all text-red-700">
                  {error.name}: {error.message}
                </p>
                {error.stack && (
                  <pre
                    dir="ltr"
                    className="text-[10px] font-mono text-gray-500 max-h-48 overflow-auto whitespace-pre-wrap leading-relaxed"
                  >
                    {error.stack}
                  </pre>
                )}
                {componentStack && (
                  <pre
                    dir="ltr"
                    className="text-[10px] font-mono text-gray-400 max-h-32 overflow-auto whitespace-pre-wrap leading-relaxed border-t border-gray-200 pt-2"
                  >
                    {componentStack.trim()}
                  </pre>
                )}
              </div>
            </details>
          </div>

          {/* Actions */}
          <div className="px-6 sm:px-8 pb-6 space-y-3">
            <button
              type="button"
              onClick={this.handleRetry}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold transition-colors"
            >
              <span className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                נסה שוב
              </span>
              <ArrowRight className="w-4 h-4 rotate-180" />
            </button>

            <button
              type="button"
              onClick={this.handleGoModuleHome}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border border-gray-200 bg-white hover:border-primary-300 text-gray-800 text-sm font-semibold transition-colors"
              title={moduleHomePath}
            >
              <span className="flex items-center gap-2">
                <Home className="w-4 h-4" />
                חזרה לדף הראשי של המודל
              </span>
              <ArrowRight className="w-4 h-4 rotate-180" />
            </button>

            <button
              type="button"
              onClick={this.handleGoAppHome}
              className="w-full text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              או חזור לדף הבית של המערכת
            </button>
          </div>
        </div>
      </div>
    );
  }
}
